import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import type { ContentId } from './contentTreeItemType.ts';

export enum MultiSelectMode {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
	DISABLED = 'disabled',
}

export interface ContentTreeSelectionState {
	count: number; // Count represents the number of selected content items and its descendants (if applicable).
	multiSelectMode: MultiSelectMode;
	// Set of content IDs for which descendant counts are currently being loaded.
	loadingDescendantIds: Set<ContentId>;
	selectedContent: Record<
		ContentId,
		{
			selectedDescendantCount: number;
			deselectedChildren: Set<ContentId>;
			parentId?: ContentId | null;
		}
	>;
}

type ItemWithParent = { id: ContentId; parentId?: ContentId };

const initialState: ContentTreeSelectionState = {
	multiSelectMode: MultiSelectMode.INACTIVE,
	selectedContent: {},
	count: 0,
	loadingDescendantIds: new Set(),
};

const actions = {
	getState:
		() =>
		({ getState }) => {
			return getState();
		},
	enterMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				multiSelectMode: MultiSelectMode.ACTIVE,
				selectedContent: {},
				count: 0,
			});
		},
	exitMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				multiSelectMode: MultiSelectMode.INACTIVE,
				selectedContent: {},
				count: 0,
				loadingDescendantIds: new Set(),
			});
		},
	disableMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				multiSelectMode: MultiSelectMode.DISABLED,
			});
		},
	getSelectedDescendantCount:
		(contentId: ContentId) =>
		({ getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			return selectedContent[contentId].selectedDescendantCount;
		},
	updateTotalCount:
		(delta: number) =>
		({ getState, setState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { count: prevCount } = getState();

			setState({
				count: prevCount + delta,
			});
		},
	// Update the selected descendant count.
	updateSelectedDescendantsCount:
		(contentId: ContentId, descendantCount: number) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			const newSelectedContent = {
				...selectedContent,
				[contentId]: {
					selectedDescendantCount: descendantCount,
					deselectedChildren:
						selectedContent[contentId]?.deselectedChildren ?? new Set<ContentId>(),
				},
			};
			setState({
				selectedContent: newSelectedContent,
			});
		},
	// Update the parent content's deselected children array.
	updateParent:
		(contentId: ContentId, parentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			const newSelectedContent = {
				...selectedContent,
				[parentId]: {
					selectedDescendantCount: selectedContent[parentId!].selectedDescendantCount,
					deselectedChildren:
						new Set(
							[...selectedContent[parentId!].deselectedChildren].filter((x) => x !== contentId),
						) ?? new Set<ContentId>(),
				},
			};

			setState({
				selectedContent: newSelectedContent,
			});
		},
	// Selects the content and its descendants, updating the selectedContent state.
	selectNestedContent:
		(items: ItemWithParent[]) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();
			// First element in items is the current item being selected.
			const currentItemId = items[0].id;

			const newSelectedContent = { ...selectedContent };
			for (const item of items) {
				if (currentItemId === item.id && item.parentId && item.parentId in newSelectedContent) {
					newSelectedContent[item.parentId].deselectedChildren.delete(item.id);
				}

				newSelectedContent[item.id] = {
					selectedDescendantCount: 0,
					deselectedChildren: selectedContent[item.id]?.deselectedChildren ?? new Set<ContentId>(),
				};
			}

			setState({
				selectedContent: newSelectedContent,
			});
		},
	// Selects the content and its descendants, updating the selectedContent state.
	incrementAncestors:
		(ancestorIds: ContentId[], currentContentSelectionCount: number, countToRemove: number) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			const newSelectedContent = ancestorIds.reduce(
				(acc, parentId) => {
					if (parentId in acc) {
						acc[parentId] = {
							...acc[parentId],
							selectedDescendantCount:
								acc[parentId].selectedDescendantCount +
								currentContentSelectionCount -
								countToRemove +
								1, // selectedDescendantCount does not include itself, hence the +1.
						};
					}
					return acc;
				},
				{ ...selectedContent },
			);

			setState({
				selectedContent: newSelectedContent,
			});
		},
	// Decrement the selected descendant count of ancestors when a content is deselected.
	decrementAncestors:
		(ancestorIds: ContentId[], currentContentId: string) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();
			const selectItemCount = selectedContent[currentContentId].selectedDescendantCount;

			const newSelectedContent = ancestorIds.reduce(
				(acc, parentId) => {
					if (parentId in acc) {
						acc[parentId] = {
							...acc[parentId],
							selectedDescendantCount: acc[parentId].selectedDescendantCount - selectItemCount - 1,
						};
					}
					return acc;
				},
				{ ...selectedContent },
			);

			setState({
				selectedContent: newSelectedContent,
			});
		},
	// Deselects the content and its descendants, updating the selectedContent state.
	deselectNestedContent:
		(contentIds: ContentId[], parentIds: ContentId[]) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();
			let newSelectedContent = { ...selectedContent };
			contentIds.forEach((contentId) => {
				// Remove the contentId from selectedContent
				const { [contentId]: removed, ...rest } = newSelectedContent;
				newSelectedContent = rest;
				// For each parentId, update its deselectedChildren set
				parentIds.forEach((parentId) => {
					if (parentId in newSelectedContent) {
						const parentContent = { ...newSelectedContent[parentId] };
						if (!parentContent.deselectedChildren) {
							parentContent.deselectedChildren = new Set<ContentId>();
						}
						parentContent.deselectedChildren.add(contentId);
						newSelectedContent[parentId] = parentContent;
					}
				});
			});

			setState({
				selectedContent: newSelectedContent,
			});
		},

	clearSelection:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				selectedContent: {},
				count: 0,
				loadingDescendantIds: new Set(),
			});
		},
	addLoadingDescendant:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const next = new Set(getState().loadingDescendantIds);
			next.add(contentId);
			setState({ loadingDescendantIds: next });
		},
	removeLoadingDescendant:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const next = new Set(getState().loadingDescendantIds);
			next.delete(contentId);
			setState({ loadingDescendantIds: next });
		},
};

export const Store = createStore<ContentTreeSelectionState, typeof actions>({
	initialState,
	actions,
	name: 'contentTreeSelection',
});

export const useContentTreeSelection = createHook(Store);
export const useContentTreeSelectionActions = createActionsHook(Store);
